import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  Check,
  X,
  ChevronsUpDown,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/* --------------------------------------------------------------------- */
/* Intl helpers (inlined to sidestep visual-edits plugin cross-file lookup) */
/* --------------------------------------------------------------------- */

const FALLBACK_CURRENCIES = [
  "USD","EUR","GBP","JPY","CNY","INR","CAD","AUD","CHF","SGD",
  "HKD","NZD","SEK","NOK","DKK","PLN","CZK","HUF","RON","TRY",
  "RUB","BRL","MXN","ARS","CLP","COP","PEN","ZAR","EGP","MAD",
  "NGN","KES","GHS","AED","SAR","QAR","KWD","BHD","OMR","JOD",
  "ILS","KRW","THB","VND","IDR","MYR","PHP","TWD","PKR","BDT",
  "LKR","NPR","MMK","KHR","LAK","MNT","UZS","KZT","GEL","AMD",
  "AZN","BYN","UAH","MDL","RSD","BGN","HRK","ISK","ALL","MKD",
  "BAM","ETB","TZS","UGX","RWF","MUR","BWP","NAD","XOF","XAF",
  "XCD","JMD","TTD","BBD","BSD","BZD","BMD","KYD","DOP","HTG",
  "GTQ","HNL","NIO","CRC","PAB","PYG","UYU","BOB","VES","FJD",
];

const metaCache = new Map();

function listAllCurrencies() {
  try {
    if (
      typeof Intl !== "undefined" &&
      typeof Intl.supportedValuesOf === "function"
    ) {
      return Intl.supportedValuesOf("currency");
    }
  } catch {
    /* fall through */
  }
  return FALLBACK_CURRENCIES;
}

function curName(code, loc) {
  try {
    return new Intl.DisplayNames([loc || "en"], { type: "currency" }).of(code) || code;
  } catch {
    return code;
  }
}

function curSymbol(code, loc) {
  try {
    const out = new Intl.NumberFormat(loc || "en", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    }).format(0);
    const sym = out.replace(/[\s\u00a0\d.,\-−()]/g, "").trim();
    return sym || code;
  } catch {
    return code;
  }
}

function curMeta(code, loc) {
  const key = code + "-" + (loc || "en");
  if (metaCache.has(key)) return metaCache.get(key);
  const meta = { code: code, name: curName(code, loc), symbol: curSymbol(code, loc) };
  metaCache.set(key, meta);
  return meta;
}

/* --------------------------------------------------------------------- */
/* BaseCurrencySetup — one-time kitchen currency picker banner            */
/* --------------------------------------------------------------------- */

export default function BaseCurrencySetup({ settings, onSaved }) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState(settings?.base_currency || "");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem("pp_base_currency_dismissed") === "1"
  );
  const wrapRef = useRef(null);

  const locale = i18n.language || "en";

  const currencies = useMemo(
    () => listAllCurrencies().map((c) => curMeta(c, locale)),
    [locale]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return currencies;
    return currencies.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q)
    );
  }, [currencies, query]);

  const selectedMeta = code ? curMeta(code, locale) : null;

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (settings?.base_currency || dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem("pp_base_currency_dismissed", "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const handleSave = async () => {
    if (!code) {
      toast.error(
        t("baseCurrency.pickOne", { defaultValue: "Pick a currency first." })
      );
      return;
    }
    try {
      setSaving(true);
      const res = await axios.put(`${API}/settings`, {
        base_currency: code,
        ...(settings?.currency ? {} : { currency: code }),
      });
      onSaved?.(res.data);
      toast.success(
        t("baseCurrency.saved", {
          defaultValue: "Kitchen currency set to {{code}}",
          code,
        })
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t("baseCurrency.failed", { defaultValue: "Couldn't save currency" })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-8 relative rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900 px-6 py-5"
      data-testid="base-currency-setup-banner"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t("common.close")}
        className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition-colors"
        data-testid="base-currency-setup-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-5">
        <div className="flex-shrink-0 h-11 w-11 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
          <Wallet className="h-5 w-5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[0.7rem] uppercase tracking-[0.25em] text-amber-400 font-semibold">
              {t("baseCurrency.oneTimeSetup", { defaultValue: "One-Time Setup" })}
            </span>
          </div>
          <h3 className="text-xl font-bold text-white mb-1">
            {t("baseCurrency.title", {
              defaultValue: "Choose your kitchen's operational currency",
            })}
          </h3>
          <p className="text-sm text-slate-400 mb-4 max-w-xl leading-relaxed">
            {t("baseCurrency.body", {
              defaultValue:
                "This drives every price, cost, and margin across your Pantry, Recipes, and Guest Menu. Your SaaS subscription is billed separately in USD and isn't affected.",
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div ref={wrapRef} className="relative flex-1 max-w-md">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-700 bg-slate-800 text-white hover:bg-slate-800/80 transition-colors"
                data-testid="base-currency-picker-trigger"
              >
                <span className="flex items-center gap-2.5 min-w-0 flex-1 text-left">
                  {selectedMeta ? (
                    <>
                      <span className="font-mono text-base w-5 shrink-0 inline-flex justify-center">
                        {selectedMeta.symbol}
                      </span>
                      <span className="truncate">
                        {selectedMeta.name}
                        <span className="ml-1 text-xs uppercase tracking-wider text-slate-400">
                          ({selectedMeta.code})
                        </span>
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-400">
                      {t("baseCurrency.placeholder", {
                        defaultValue: "Search 180+ world currencies…",
                      })}
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>

              {open && (
                <div
                  className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-slate-700 bg-slate-900 shadow-lg overflow-hidden"
                  data-testid="base-currency-picker-panel"
                  role="listbox"
                >
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-slate-950">
                    <Search className="h-4 w-4 shrink-0 text-slate-400" />
                    <input
                      type="text"
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("currencyPicker.search", {
                        defaultValue: "Search currencies…",
                      })}
                      className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
                      data-testid="base-currency-picker-search"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="text-slate-400"
                        aria-label="Clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    {filtered.length === 0 ? (
                      <div className="px-3 py-6 text-center text-sm text-slate-400">
                        {t("currencyPicker.empty", {
                          defaultValue: "No currencies match.",
                        })}
                      </div>
                    ) : (
                      filtered.map((c) => {
                        const active = code === c.code;
                        return (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => {
                              setCode(c.code);
                              setOpen(false);
                              setQuery("");
                            }}
                            className={cn(
                              "w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-800",
                              active
                                ? "bg-slate-800 text-amber-400 font-semibold"
                                : "text-white"
                            )}
                            data-testid={"base-currency-picker-option-" + c.code}
                            role="option"
                            aria-selected={active}
                          >
                            <span className="font-mono text-base w-6 shrink-0 text-center">
                              {c.symbol}
                            </span>
                            <span className="flex-1 truncate">{c.name}</span>
                            <span className="text-[0.65rem] uppercase tracking-wider shrink-0 text-slate-400">
                              {c.code}
                            </span>
                            {active && <Check className="ml-1 h-4 w-4 shrink-0" />}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || !code}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-5 gap-2 sm:w-auto"
              data-testid="base-currency-save"
            >
              <Check className="h-4 w-4" />
              {saving
                ? t("common.saving")
                : t("baseCurrency.confirm", {
                    defaultValue: "Set as kitchen currency",
                  })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
